import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors, } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy, } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import FieldEditor from './FieldEditor';
const QUICK_ADD = [
    { type: 'text', label: 'Text', group: 'Text', icon: '✎' },
    { type: 'email', label: 'Email', group: 'Text', icon: '✉' },
    { type: 'tel', label: 'Phone', group: 'Text', icon: '☎' },
    { type: 'url', label: 'URL', group: 'Text', icon: '🔗' },
    { type: 'textarea', label: 'Long text', group: 'Text', icon: '¶', defaults: { rows: 4 } },
    { type: 'number', label: 'Number', group: 'Numeric', icon: '#' },
    { type: 'date', label: 'Date', group: 'Numeric', icon: '📅' },
    { type: 'select', label: 'Dropdown', group: 'Choice', icon: '▾', defaults: { options: ['Option 1', 'Option 2'] } },
    { type: 'radio', label: 'Radio', group: 'Choice', icon: '◉', defaults: { options: ['Option 1', 'Option 2'] } },
    { type: 'multiselect', label: 'Multi-choice', group: 'Choice', icon: '☑', defaults: { options: ['Option 1', 'Option 2'] } },
    { type: 'checkbox', label: 'Checkbox', group: 'Choice', icon: '☐' },
    { type: 'rating', label: 'Rating', group: 'Choice', icon: '★', defaults: { scale_max: 5 } },
    { type: 'gdpr', label: 'GDPR', group: 'Special', icon: '⚖', defaults: { required: true, consent_text: 'I agree to the processing of my data.' } },
    { type: 'hidden', label: 'Hidden', group: 'Special', icon: '⌗' },
];
export default function FormBuildTab({ draft, setDraft }) {
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
    const [selectedId, setSelectedId] = useState(() => draft.fields[0]?.id ?? null);
    // If the selected field disappears (deleted, type-change, etc.), pick another.
    useEffect(() => {
        if (!selectedId)
            return;
        if (!draft.fields.find((f) => f.id === selectedId)) {
            setSelectedId(draft.fields[0]?.id ?? null);
        }
    }, [draft.fields, selectedId]);
    const selected = useMemo(() => draft.fields.find((f) => f.id === selectedId) ?? null, [draft.fields, selectedId]);
    function setFields(fields) { setDraft({ ...draft, fields }); }
    function addField(qa) {
        const id = freshFieldId(qa.type, draft.fields.map((f) => f.id));
        const isHidden = qa.type === 'hidden';
        const f = {
            id,
            type: qa.type,
            label: isHidden ? '' : qa.label,
            placeholder: '',
            required: qa.defaults?.required ?? false,
            help: '',
            rows: qa.defaults?.rows ?? 3,
            options: qa.defaults?.options ?? [],
            ...(qa.defaults ?? {}),
        };
        setFields([...draft.fields, f]);
        setSelectedId(id);
    }
    function updateField(id, next) {
        // If the user renamed the field id, keep the selection following the field.
        setFields(draft.fields.map((f) => f.id === id ? next : f));
        if (id !== next.id)
            setSelectedId(next.id);
    }
    function deleteField(id) {
        if (!window.confirm(`Remove field "${id}"?`))
            return;
        setFields(draft.fields.filter((f) => f.id !== id));
        if (selectedId === id)
            setSelectedId(null);
    }
    function duplicateField(id) {
        const src = draft.fields.find((f) => f.id === id);
        if (!src)
            return;
        const newId = freshFieldId(src.type, draft.fields.map((f) => f.id));
        const idx = draft.fields.findIndex((f) => f.id === id);
        const cloned = { ...src, id: newId };
        const next = [...draft.fields];
        next.splice(idx + 1, 0, cloned);
        setFields(next);
        setSelectedId(newId);
    }
    function handleDragEnd(e) {
        const { active, over } = e;
        if (!over || active.id === over.id)
            return;
        const from = draft.fields.findIndex((f) => f.id === active.id);
        const to = draft.fields.findIndex((f) => f.id === over.id);
        if (from < 0 || to < 0)
            return;
        setFields(arrayMove(draft.fields, from, to));
    }
    const groupedAdd = useMemo(() => groupBy(QUICK_ADD, (q) => q.group), []);
    return (_jsxs("div", { className: "form-build-shell", children: [_jsxs("div", { className: "build-add-bar", children: [_jsx("span", { className: "build-add-bar-label", children: "Add field:" }), Object.entries(groupedAdd).map(([group, items]) => (_jsx("div", { className: "build-add-group", title: group, children: items.map((q) => (_jsxs("button", { type: "button", className: "build-add-chip", onClick: () => addField(q), title: `Add ${q.label}`, children: [_jsx("span", { className: "build-add-icon", children: q.icon }), _jsx("span", { className: "build-add-label", children: q.label })] }, q.type))) }, group))), _jsxs("span", { className: "build-add-meta muted small", children: [draft.fields.length, " field", draft.fields.length === 1 ? '' : 's'] })] }), _jsxs("div", { className: "build-master-detail", children: [_jsxs("aside", { className: "build-list-pane", children: [_jsxs("div", { className: "build-list-head", children: [_jsx("span", { children: "Fields" }), _jsxs("span", { className: "muted small", children: ["drag ", _jsx("code", { children: "\u22EE\u22EE" }), " to reorder"] })] }), draft.fields.length === 0 ? (_jsxs("div", { className: "build-empty muted small", children: [_jsx("strong", { children: "No fields yet." }), _jsx("p", { children: "Pick one from the bar above to get started." })] })) : (_jsx(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragEnd: handleDragEnd, children: _jsx(SortableContext, { items: draft.fields.map((f) => f.id), strategy: verticalListSortingStrategy, children: _jsx("ul", { className: "build-field-list", children: draft.fields.map((f) => (_jsx(FieldRow, { field: f, selected: selectedId === f.id, onSelect: () => setSelectedId(f.id), onDuplicate: () => duplicateField(f.id), onDelete: () => deleteField(f.id) }, f.id))) }) }) }))] }), _jsx("main", { className: "build-detail-pane", children: selected ? (_jsxs("div", { className: "build-detail-card", children: [_jsxs("header", { className: "build-detail-head", children: [_jsxs("div", { className: "build-detail-title", children: [_jsx("span", { className: "build-detail-icon", children: iconFor(selected.type) }), _jsxs("div", { children: [_jsx("h3", { children: selected.label || _jsx("em", { className: "muted", children: "(unlabeled)" }) }), _jsx("code", { className: "muted small", children: `#${selected.id} · ${selected.type}` })] })] }), _jsxs("div", { className: "build-detail-actions", children: [_jsx("button", { type: "button", className: "ghost small", onClick: () => duplicateField(selected.id), children: "\u2398 Duplicate" }), _jsx("button", { type: "button", className: "ghost small danger", onClick: () => deleteField(selected.id), children: "\u2715 Remove" })] })] }), _jsx("div", { className: "build-detail-body", children: _jsx(FieldEditor, { field: selected, onChange: (next) => updateField(selected.id, next) }) })] })) : (_jsx("div", { className: "build-detail-empty", children: _jsxs("div", { className: "build-detail-empty-inner", children: [_jsx("h3", { children: "Pick a field" }), _jsx("p", { className: "muted", children: "Choose a field on the left to edit its label, type, validation, and per-type options \u2014 or click a chip in the bar above to add a new one." })] }) })) })] })] }));
}
function FieldRow({ field, selected, onSelect, onDuplicate, onDelete }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.55 : 1,
    };
    return (_jsxs("li", { ref: setNodeRef, style: style, className: `build-field-row${selected ? ' selected' : ''}`, children: [_jsx("span", { className: "build-field-drag", ...attributes, ...listeners, title: "Drag to reorder", children: "\u22EE\u22EE" }), _jsxs("button", { type: "button", className: "build-field-row-btn", onClick: onSelect, children: [_jsx("span", { className: "build-field-icon", children: iconFor(field.type) }), _jsxs("span", { className: "build-field-text", children: [_jsx("strong", { children: field.label || _jsx("em", { className: "muted", children: "(unlabeled)" }) }), _jsxs("span", { className: "muted small", children: [field.id, " \u00B7 ", field.type] })] }), field.required && _jsx("span", { className: "tag warn-tag", style: { flexShrink: 0 }, children: "required" })] }), _jsxs("span", { className: "build-field-row-actions", children: [_jsx("button", { type: "button", className: "ghost small", onClick: onDuplicate, title: "Duplicate field", children: "\u2398" }), _jsx("button", { type: "button", className: "ghost small danger", onClick: onDelete, title: "Remove field", children: "\u2715" })] })] }));
}
// ---- helpers ----
function freshFieldId(type, used) {
    const base = type.replace(/[^a-z0-9_]/gi, '_').toLowerCase() || 'field';
    if (!used.includes(base))
        return base;
    let i = 2;
    while (used.includes(`${base}_${i}`))
        i++;
    return `${base}_${i}`;
}
function iconFor(type) {
    const map = {
        text: '✎', email: '✉', tel: '☎', url: '🔗',
        number: '#', date: '📅', textarea: '¶',
        select: '▾', radio: '◉', multiselect: '☑',
        checkbox: '☐', rating: '★', hidden: '⌗', gdpr: '⚖',
    };
    return map[type] ?? '·';
}
function groupBy(items, by) {
    return items.reduce((acc, item) => {
        const k = by(item);
        (acc[k] ||= []).push(item);
        return acc;
    }, {});
}
